// Local history of finished computer games (plan §9: bot games are fully
// client-side). We keep them in localStorage rather than the server — there's no
// opponent account and no rating at stake — so they can be listed in My games and re-opened on a passive replay board (with analysis available as an
// explicit option). Best-effort: storage may be unavailable (private mode) or
// full; the finished board keeps an in-memory record when saving fails.

import type { Color, MoveHist } from "./types.ts";

export interface BotGameRecord {
  id: string;
  createdAt: number;
  level: number;
  humanColor: Color;
  tc: string; // "5+3" or "unlimited"
  status: string; // mate | timeout | resign | stalemate | insufficient | threefold | fifty | draw
  winner: Color | null;
  result: "won" | "lost" | "draw"; // from the human's perspective
  moves: MoveHist[];
}

const KEY = "chess.botGames";
const MAX = 24;

export function listBotGames(): BotGameRecord[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as BotGameRecord[];
    return Array.isArray(arr) ? arr.filter((game): game is BotGameRecord =>
      !!game && typeof game.id === "string" && /^bot_[A-Za-z0-9]+$/.test(game.id) &&
      Number.isFinite(game.createdAt) && Number.isInteger(game.level) && game.level >= 1 && game.level <= 8 &&
      ["white","black"].includes(game.humanColor) && typeof game.tc === "string" &&
      ["won","lost","draw"].includes(game.result) && typeof game.status === "string" &&
      (game.winner === null || ["white","black"].includes(game.winner)) && Array.isArray(game.moves) &&
      game.moves.every(move => !!move && typeof move.uci === "string" && /^[a-h][1-8][a-h][1-8][qrbn]?$/.test(move.uci) && typeof move.san === "string")
    ).slice(0, MAX) : [];
  } catch {
    return [];
  }
}

export function getBotGame(id: string): BotGameRecord | undefined {
  return listBotGames().find((g) => g.id === id);
}

/** Wipe the local computer-game history (used by the superuser DB reset, which
 *  otherwise leaves this client-side history in place). Best-effort. */
export function clearBotGames(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Return the record even on failure, so the current board can review/export it. */
export function saveBotGame(rec: Omit<BotGameRecord, "id" | "createdAt">): { record: BotGameRecord; saved: boolean } {
  const id = `bot_${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
  const full: BotGameRecord = { ...rec, id, createdAt: Date.now() };
  try {
    const all = [full, ...listBotGames()].slice(0, MAX);
    localStorage.setItem(KEY, JSON.stringify(all));
    return { record: full, saved: true };
  } catch {
    return { record: full, saved: false };
  }
}
